import axios from "axios";
import { ApiResponse } from "@/types";

export interface ReferredUser {
    id: string;
    email: string;
    referralId: string;
    createdAt: string;
}

export interface ReferralBonus {
    id: string;
    userId: string;
    bonusProperties: any[];
}

export interface CreateReferredUserParams {
    userEmail: string;
    referralId: string;
}

export interface GetReferralBonusParams {
    userId: string;
    date?: string | null;
}

export interface GetReferredUsersParams {
    userId: string;
}

export interface UpdateReferredUsersParams {
    userId: string;
    referralIds: string[];
}

/**
 * Creates a referred user instance.
 *
 * @param params - Parameters for creating a referred user
 * @returns The response data from the server.
 * @throws Throws an error if the request fails.
 */
export const createReferredUser = async ({
    userEmail,
    referralId,
}: CreateReferredUserParams): Promise<ApiResponse<ReferredUser>> => {
    return await axios.post(`/api/referrals/${referralId}/users`, { referredUserEmail: userEmail });
};

/**
 * Retrieves referral bonus information associated with the specified user ID.
 * @param options - An object containing options.
 * @returns A promise that resolves with the referral bonus information.
 */
export const getReferralBonus = async ({
    userId,
    date = null,
}: GetReferralBonusParams): Promise<ApiResponse<ReferralBonus>> => {
    return await axios.get(`/api/bonus-referral-properties/users/${userId}`, {
        params: {
            date,
        },
    });
};

/**
 * Retrieves referred users associated with the specified user ID.
 * @param options - An object containing options.
 * @returns A promise that resolves with the referred users information.
 */
export const getReferredUsers = async ({
    userId,
}: GetReferredUsersParams): Promise<ApiResponse<{ referredUsers: ReferredUser[] }>> => {
    return await axios.get(`/api/referrals/user-referrals/${userId}`);
};

/**
 * Updates the viewed status for referred users associated with the specified user ID.
 * @param options - An object containing options.
 * @returns A promise that resolves when the referred users are successfully updated.
 */
export const updateReferredUsers = async ({
    userId,
    referralIds,
}: UpdateReferredUsersParams): Promise<ApiResponse<void>> => {
    return await axios.patch(`/api/referrals/user-referrals/${userId}`, { referralIds });
};
